import { useState } from 'react';
import { useFinanceAuditLogs, useAuditLogEntityTypes, useAuditLogActions } from '@/hooks/queries/useFinanceAuditLogs';
import { AuditDiff } from './AuditDiff';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getActionLabel } from './auditActionLabels';

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return isoString;
  }
}

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: entityTypes } = useAuditLogEntityTypes();
  const { data: actions } = useAuditLogActions();
  const { data: auditData, isLoading, error } = useFinanceAuditLogs({
    page,
    pageSize: 20,
    entityType: entityType || undefined,
    action: action || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const entries = auditData?.items || [];
  const totalCount = auditData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / 20);

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="mt-1 text-gray-600">Review all changes made to your campaign finance records.</p>
      </div>

      {/* Filter Bar */}
      <div className="grid gap-4 rounded-lg border bg-gray-50 p-4 md:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Entity Type</label>
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(1);
            }}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All entity types</option>
            {entityTypes?.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Action</label>
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All actions</option>
            {actions?.map((act) => (
              <option key={act} value={act}>
                {getActionLabel(act)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            placeholder="From date"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            placeholder="To date"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        {isLoading && (
          <div className="p-8 text-center text-gray-500">Loading audit log...</div>
        )}

        {error && (
          <div className="p-8 text-center text-red-600">Failed to load audit log. Please try again.</div>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <div className="p-8 text-center text-gray-500">No audit log entries.</div>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-6 py-3 text-left text-sm font-semibold">Timestamp</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">User</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Entity Type</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Action</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      className="cursor-pointer border-b hover:bg-gray-50"
                    >
                      <td className="px-6 py-3 text-sm">{formatTimestamp(entry.timestamp)}</td>
                      <td className="px-6 py-3 text-sm">{entry.userName}</td>
                      <td className="px-6 py-3 text-sm">{entry.entityType}</td>
                      <td className="px-6 py-3 text-sm">{getActionLabel(entry.action)}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{entry.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expanded Diff Row */}
            {expandedId && (
              <div className="border-t bg-gray-50 px-6 py-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Changes</div>
                {entries.find((e) => e.id === expandedId) && (
                  <AuditDiff
                    oldValue={entries.find((e) => e.id === expandedId)?.oldValue ?? null}
                    newValue={entries.find((e) => e.id === expandedId)?.newValue ?? null}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !error && entries.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
